import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, Edit, Trash2, Search, ShoppingCart, Palette } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { enumsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function SaleStatuses() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 100
  })
  const [deleteId, setDeleteId] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    name_az: '',
    name_ru: '',
    name_en: '',
    name_ko: '',
    name_kk: '',
    name_ky: '',
    bg_color: ''
  })
  const [togglingId, setTogglingId] = useState(null)
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['sale-statuses', filters],
    () => enumsApi.saleStatuses.getAll(filters),
    {
      select: (data) => data.data?.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const deleteMutation = useMutation(
    (id) => enumsApi.saleStatuses.delete(id),
    {
      onSuccess: () => {
        toast.success('Sale status deleted successfully')
        queryClient.invalidateQueries(['sale-statuses'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete sale status')
      }
    }
  )

  const createMutation = useMutation(
    (data) => enumsApi.saleStatuses.create(data),
    {
      onSuccess: () => {
        toast.success('Sale status created successfully')
        queryClient.invalidateQueries(['sale-statuses'])
        setShowForm(false)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create sale status')
      }
    }
  )

  const updateMutation = useMutation(
    (data) => enumsApi.saleStatuses.update(editingId, data),
    {
      onSuccess: () => {
        toast.success('Sale status updated successfully')
        queryClient.invalidateQueries(['sale-statuses'])
        setShowForm(false)
        setEditingId(null)
        resetForm()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update sale status')
      }
    }
  )

  const toggleStatusMutation = useMutation(
    (id) => {
      setTogglingId(id)
      return enumsApi.saleStatuses.toggleStatus(id)
    },
    {
      onSuccess: (response) => {
        toast.success(response.data?.message || 'Sale status status updated successfully')
        queryClient.invalidateQueries(['sale-statuses'])
        setTogglingId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to toggle sale status status')
        setTogglingId(null)
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this sale status?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleEdit = (saleStatus) => {
    setEditingId(saleStatus.id)
    setFormData({
      name_az: saleStatus.name_az,
      name_ru: saleStatus.name_ru,
      name_en: saleStatus.name_en,
      name_ko: saleStatus.name_ko,
      name_kk: saleStatus.name_kk || saleStatus.name_ru,
      name_ky: saleStatus.name_ky || saleStatus.name_ru,
      bg_color: saleStatus.bg_color
    })
    setShowForm(true)
  }

  const resetForm = () => {
    setFormData({
      name_az: '',
      name_ru: '',
      name_en: '',
      name_ko: '',
      name_kk: '',
      name_ky: '',
      bg_color: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (editingId) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleToggleStatus = (id) => {
    toggleStatusMutation.mutate(id)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sale Statuses</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage sale status options for vehicles
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Sale Status
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingId ? 'Edit Sale Status' : 'Add New Sale Status'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Hex Code *</label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="color"
                        value={formData.bg_color}
                        onChange={(e) => handleInputChange('bg_color', e.target.value)}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        placeholder="#ffffff"
                        value={formData.bg_color}
                        onChange={(e) => handleInputChange('bg_color', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <h3 className="text-lg mt-6 font-medium text-gray-900 mb-4">Names in Different Languages</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(languageLabels).map(([lang, label]) => (
                    <div key={lang}>
                      <label className="label">
                        Name ({label}) *
                        {lang === currentLanguage && (
                          <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                        )}
                      </label>
                      <Input
                        placeholder={`Enter name in ${label}`}
                        value={formData[`name_${lang}`]}
                        onChange={(e) => handleInputChange(`name_${lang}`, e.target.value)}
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false)
                    setEditingId(null)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Sale Statuses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search sale statuses..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Sale Statuses ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading sale statuses: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Background</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((saleStatus) => (
                  <TableRow key={saleStatus.id}>
                    <TableCell>
                      <div className="flex items-center">
                        <ShoppingCart className="h-4 w-4 mr-2 text-gray-400" />
                        {saleStatus[`name_${currentLanguage}`] || saleStatus.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleStatus(saleStatus.id)}
                        disabled={togglingId === saleStatus.id}
                        className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                        title="Click to toggle status"
                      >
                        {togglingId === saleStatus.id ? (
                          <div className="flex items-center">
                            <div className="loading-spinner mr-2" />
                            <Badge variant={saleStatus.is_active ? 'success' : 'gray'}>
                              {saleStatus.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        ) : (
                          <Badge variant={saleStatus.is_active ? 'success' : 'gray'}>
                            {saleStatus.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-6 h-6 rounded-full border border-gray-300"
                          style={{ backgroundColor: saleStatus.bg_color }}
                        ></div>
                        <Palette className="h-4 w-4 text-gray-400" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(saleStatus)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(saleStatus.id)}
                          disabled={deleteMutation.isLoading && deleteId === saleStatus.id}
                        >
                          {deleteMutation.isLoading && deleteId === saleStatus.id ? (
                            <div className="loading-spinner" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No sale statuses</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first sale status.
              </p>
              <div className="mt-6">
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Sale Status
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

